import * as React from 'react';
import { FavouriteDefinition } from 'app/types-shared';
import { AxisLabelMode, RiskMatrix } from 'app/components';
import {
  useFavouriteLayoutAttributes, useRiskMatrixListingEvents, useSelectedDrillDownReport, useSelectors
} from 'app/area/case-report/risk-listing/hooks';
import {
  fillMissingAxis, getAxisDescription, mapToRiskMatrixPredefinedValues
} from 'app/area/risk-matrix-administration/mappers/map-to-risk-matrix-definition';
import { RiskReportResults } from 'app/area/case-report/risk-listing/types-store';
import { matrixRowHeight } from 'app/area/risk-matrix-administration/constants';
import { ElementLoader, InfoText, SelectSingle } from 'ui';
import caseListingstyles from './../case-listing/components/case-listing.scss';
import { CaseReportListingInformation } from 'app/area/case-report/components/case-report-listing-information';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'app/types-store';
import { AttributesPicker } from 'app/area/reports/containers/chart/components/attributes';
import { presentationType } from 'app/area/case-report/constants';
import styles from './risk-matrix-listing.scss';
import { useMemo } from 'react';
import { replaceTemplateString } from 'app/infrastructure/platform';
import { useInstanceOptions } from 'app/area/favourite/favourite-context';
import { reportLayoutTypeContext } from 'app/area/reports/constants';

interface RiskMatrixListingProps {
  translation: any;
  autoExecute: boolean;
  toggleAutoExecution: () => void;
  riskReportResults?: RiskReportResults;
  fetching: boolean;
  workingDefinition: FavouriteDefinition;
  hideVisualizationOptions: boolean;
  executeFailed?: JSX.Element;
  height?: number;
}

export const RiskMatrixListing = ({
  translation,
  autoExecute,
  riskReportResults,
  fetching,
  workingDefinition,
  executeFailed,
  hideVisualizationOptions
}: RiskMatrixListingProps) => {

  const locale = useSelector((state: ApplicationState) => state?.app.locale);

  const {
    height,
    layoutTypeContextId = reportLayoutTypeContext
  } = useInstanceOptions();

  const {
    reportApiOverrides
  } = useInstanceOptions();

  const { onDrilldown, onUpdateLayoutAttributes, onLoadOptionsDrillDown, onSetDrillDownReport } = useRiskMatrixListingEvents(
    workingDefinition,
    autoExecute,
    layoutTypeContextId,
    riskReportResults,
    reportApiOverrides
  );

  const { layoutAttributes } = useFavouriteLayoutAttributes(layoutTypeContextId);
  const selectedDrillDownReport = useSelectedDrillDownReport();
  const { fetchingOptionsDrillDownTo, optionsDrillDownTo } = useSelectors();

  const noResults = !!riskReportResults && (riskReportResults.matrixDefs.length === 0 ||
    riskReportResults.results[0]?.riskMatrices.length === 0);
  // Risk report result contains more than one risk matrix to be rendered
  const resultHasManyRiskMatrices = riskReportResults?.results.length > 1 || riskReportResults?.results[0]?.riskMatrices?.length > 1;

  const riskMatrices = useMemo(() => {
    // Display message only when both attributes are false. Undefined is the default value processed in attributes mapping as true.
    const shouldDisplaySelectAttributesMessage = layoutAttributes?.['RiskReport.LossPotential'] === false &&
      layoutAttributes?.['RiskReport.ResidualLossPotential'] === false;

    if (shouldDisplaySelectAttributesMessage) {
      return <div className={`${styles.fullwidthContainer}`}>
        <div className="alert alert-warning">
          <p>{translation.warning_select_residual_or_loss_potential}</p>
        </div>
      </div>;
    }

    const rows: React.ReactNode[] = [];
    riskReportResults?.matrixDefs?.map((matrixDefinition, matrixDefinitionIndex) => {
      const isRiskMatrixTypeUnsupported = matrixDefinition.axes.length > 2;
      const riskMatrixUnsupportedMessage = `${replaceTemplateString(translation.risk_matrix_not_supported, matrixDefinition.type)}`;

      const columns = isRiskMatrixTypeUnsupported ?
        <div key={`alert-warning-${matrixDefinitionIndex}`} className="alert alert-warning">
          <p>{riskMatrixUnsupportedMessage}</p>
        </div>
        :
        riskReportResults?.results?.map(result => {
          const riskMatrixTypeResult = result?.riskMatrices.find(rm => rm.riskMatrixType === matrixDefinition.type);
          const onSelectedCell = (xAxisItemIndex: number, yAxisItemIndex: number) => {
            const drillDownData = riskMatrixTypeResult?.cells['x' + xAxisItemIndex + 'y' + yAxisItemIndex]?.data.drillDownValuesAndDomains;
            !!drillDownData && onDrilldown(workingDefinition, drillDownData);
          };
          const mappedRiskMatrixDefinition = { ...matrixDefinition, axes: fillMissingAxis(matrixDefinition.axes) };
          const textXAxisTitle = getAxisDescription(matrixDefinition?.axes[0].data.codetableId, translation);
          const textYAxisTitle = getAxisDescription(matrixDefinition?.axes[1]?.data.codetableId, translation);

          return !riskMatrixTypeResult ? null :
            <div className={`${styles.riskMatrixReportCellWrapper}`} key={`${result.description}-${matrixDefinition.type}`}>
              <RiskMatrix
                className={styles.riskMatrixListing}
                componentId="risk_matrix"
                onSelectedXChanged={undefined}
                onSelectedYChanged={undefined}
                onSelectedCell={onSelectedCell}
                optionToCellValue={undefined}
                preselectedValues={mapToRiskMatrixPredefinedValues(undefined)}
                displayAllValues={true}
                readOnly={false}
                riskMatrixDefinition={mappedRiskMatrixDefinition}
                riskMatrixTypeResult={riskMatrixTypeResult}
                xAxisLabelMode={AxisLabelMode.all}
                yAxisLabelMode={AxisLabelMode.all}
                textXAxisTitle={textXAxisTitle}
                textYAxisTitle={textYAxisTitle}
                rowHeight={matrixRowHeight}
                locale={locale}
                title={`${riskReportResults.results.length > 1 ? result.description : ''}`}
                layoutAttributes={layoutAttributes}
                height={height}
                useSpacing={!resultHasManyRiskMatrices}
              />
            </div>;
        });

      rows.push(<div className={`${styles.riskMatrixReportCell}`}
        key={`${matrixDefinition.type}-${matrixDefinitionIndex}`}>
        {riskReportResults?.matrixDefs?.length > 1 && <div className={`${styles.title}`}>
          <p className={`${styles.header}`}>{matrixDefinition.description}</p>
        </div>}
        {columns}
      </div>);
    }
    );
    return rows;
  }, [riskReportResults, onDrilldown]);

  if (executeFailed) {
    return executeFailed;
  }

  const resultOrInfo = autoExecute ? !noResults && riskMatrices : <div className={`${styles.riskMatrixReportCell}`}>
    <CaseReportListingInformation translation={translation} />
  </div>;

  return <div className={`${styles.riskMatrixReportRow} ${resultHasManyRiskMatrices ? 'many-risk-matrix-results' : ''}`}>
    {!hideVisualizationOptions && <div className={`${styles.fullwidthContainer} ${styles.optionsContainer}`}>
      <div className={styles.drilldownToWrapper}>
        <span className={styles.outerLabel}>{translation?.drill_down_to}
          <InfoText className={styles.infoTextWrapper} visible={true} text={translation.drill_down_to_info} />
        </span>
        <SelectSingle
          componentId={'drill-down-to'}
          className={styles.drilldownTo}
          valueItem={selectedDrillDownReport}
          options={optionsDrillDownTo}
          onLoadOptions={onLoadOptionsDrillDown}
          onChange={onSetDrillDownReport}
          textOptionsDone={translation?.drill_down_to_done}
          fetching={fetchingOptionsDrillDownTo}
        />
      </div>
      <AttributesPicker
        presentationType={presentationType.riskMatrixListing}
        onAttributeChange={onUpdateLayoutAttributes}
        translations={translation}
        layoutAttributes={layoutAttributes}
      />
    </div>}
    {fetching
      ? <ElementLoader />
      : noResults && <div className={`${caseListingstyles.uiSearchLoader} ${styles.fullwidthContainer}`}>
        {translation.search_no_hits}
      </div>}
    {resultOrInfo}
  </div>;
};
